import { getAuth } from "firebase/auth";
import { onSnapshot, query, collection, where, orderBy, getDocs } from "firebase/firestore";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { firestore, server } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";
import { PERMISSIONS } from "../../constants";

const handleExpedient = async (token, body) => {
  const response = await fetch("https://" + server + ".cloudfunctions.net/handleExpedient", {
    method: "post",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  if (response.status === 401) {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(token));
  }

  return response;
};

export const addExpedientType = async(expedientType, user) => {
  try {
    const userBody = { action: "CREATE_EXPEDIENT_TYPE", data: expedientType };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getExpedientTypes = async(user) => {
  try {
    const userBody = {
      action: "GET_EXPEDIENT_TYPES",
      ...(!user.permissions.includes(PERMISSIONS.EXPEDIENT.LIST) ? { owner: user.userId } : {})
    };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getExpedientType = async(expedientTypeId, user) => {
  try {
    const userBody = { action: "GET_EXPEDIENT_TYPE", expedientTypeId };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const addExpedient = async(expedient, user) => {
  // Validating expedient does not exist
  const exist = await getExpedient(expedient.expedientId, user);
  if (exist) throw new Error('Expedient already exists');

  const userBody = { action: "CREATE_EXPEDIENT", data: expedient };
  let response = await handleExpedient(user.token, userBody);
  response = await response.json();
  if (response.result) {
    return response.body;
  } else {
    return null;
  }
};

export const updateExpedient = async(expedient, user) => {
  try {
    const data = { ...expedient };
    delete data.createdAt;
    delete data.updatedAt;
    const userBody = { action: "UPDATE_EXPEDIENT", expedientId: expedient.expedientId, data };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getExpedients = async(user, owner) => {
  try {
    const userBody = { action: "GET_EXPEDIENTS", owner };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getExpedientsByValue = async(value, expedientTypeId, user) => {
  try {
    const userBody = { action: "EXPEDIENTS_BY_VALUE", expedientTypeId, value };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getExpedient = async(expedientId, user) => {
  try {
    const userBody = { action: "GET_EXPEDIENT", expedientId };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const addDocument = async(document, user) => {
  try {
    const userBody = {
      action: "CREATE_DOCUMENT",
      data: document,
      // Identidad del usuario que realiza la carga, para el log de auditoría (Req 7).
      actor: { userId: user.userId, name: `${user.name || ""} ${user.lastName || ""}`.trim() },
    };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getDocuments = async(expedientId, user) => {
  try {
    const userBody = { action: "DOCUMENTS_BY_FIELD", field: 'expedientId', value: expedientId};
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getDocument = async(documentId, user) => {
  try {
    const userBody = { action: "GET_DOCUMENT", documentId };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const updateDocument = async(document, user) => {
  try {
    const userBody = { action: "UPDATE_DOCUMENT", documentId: document.documentId, data: document };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

// Req 6: escucha en tiempo real los documentos de un expediente (onSnapshot).
export const getExpedientDocumentsOnSnapshot = (expedientId, callback) => {
  const q = query(
    collection(firestore, "expedient_documents"),
    where("expedientId", "==", expedientId)
  );
  return onSnapshot(q, (snapshot) => {
    const documents = snapshot.docs.map((doc) => ({
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || null,
      updatedAt: doc.data().updatedAt?.toDate?.() || null,
    }));
    callback(documents);
  });
};

// Req 6: control por usuario del "último visto" de un expediente (localStorage).
// Una novedad existe si el expediente tiene documentos creados después de la última visita.
const lastViewedKey = (userId, expedientId) => `expedientViewed:${userId}:${expedientId}`;

export const getExpedientLastViewed = (userId, expedientId) => {
  const raw = localStorage.getItem(lastViewedKey(userId, expedientId));
  return raw ? Number(raw) : 0;
};

export const markExpedientViewed = (userId, expedientId) => {
  localStorage.setItem(lastViewedKey(userId, expedientId), String(Date.now()));
};

// Devuelve true si el expediente tiene documentos nuevos respecto a la última visita del usuario.
export const expedientHasNewDocuments = (expedient, userId) => {
  if (!expedient?.lastDocumentAt) return false;
  const lastDocMs = new Date(expedient.lastDocumentAt).getTime();
  return lastDocMs > getExpedientLastViewed(userId, expedient.expedientId);
};

// Req 7: consulta del log de auditoría de carga de documentos, con filtros opcionales por
// usuario, expediente y rango de fechas. Devuelve los registros ordenados por fecha desc.
export const getAuditLogs = async(filters = {}) => {
  try {
    const constraints = [];
    if (filters.userId) constraints.push(where("userId", "==", filters.userId));
    if (filters.expedientId) constraints.push(where("expedientId", "==", filters.expedientId));
    if (filters.startDate) constraints.push(where("timestamp", ">=", filters.startDate));
    if (filters.endDate) constraints.push(where("timestamp", "<=", filters.endDate));
    constraints.push(orderBy("timestamp", "desc"));

    const q = query(collection(firestore, "audit_logs"), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || null,
    }));
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getTRD = async(user) => {
  try {
    const userBody = { action: "GET_TRD" };
    let response = await handleExpedient(user.token, userBody);
    response = await response.json();
    if (response.result) {
      return response.body;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};
